import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, CircularProgress, Divider, IconButton, Alert,
    useMediaQuery, useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    BarChart as BarChartIcon,
    PeopleAlt as PeopleAltIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Refresh as RefreshIcon,
    FileDownloadOutlined as FileDownloadIcon,
} from '@mui/icons-material';
import { useQueryResult } from '../../../../hooks/QueryBuilder/useQueryResult';
import { useQueryCount } from '../../../../hooks/QueryBuilder/useQueryCount';
import { useExtractPersonQuery } from '../../../../hooks/QueryBuilder';
import { useUser } from '../../../../context/UserContext';
import { useLanguage } from '../../../../context/LanguageContext';
import { toast } from 'react-toastify';
import NotificationService, { Notification } from '../../../../services/NotificationService';
import CountCards from './CountCards';
import PersonIdsTable from './PersonIdsTable';
import QueryExportDialog from './QueryExportDialog';

interface QueryResultDialogProps {
    open: boolean;
    onClose: () => void;
    queryId: string | number | null;
    description?: string;
}

function QueryResultDialog({
    open,
    onClose,
    queryId,
    description: initialDescription,
}: QueryResultDialogProps) {
    const { t, formatLocale } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const { userInfos } = useUser();
    const { fetchQueryResult, description: fetchedDescription, loading, error, result, reset, markAsRunning } = useQueryResult();
    const { launchQueryCount, loading: relaunchingCount, error: relaunchError, reset: resetCount } = useQueryCount();
    const { extractPersonQuery, loading: exporting, error: exportError, reset: resetExport } = useExtractPersonQuery();

    // The API result may carry its own description once fetched; until then, fall back to the
    // description supplied by the caller (e.g. the row's saved label) so the title isn't blank.
    const description = fetchedDescription || initialDescription;

    const normalizedStatus = result?.status?.toUpperCase();
    const isExpired = normalizedStatus?.includes('EXPIRED') || normalizedStatus?.includes('UNKNOWN');
    const isError = normalizedStatus === 'ERROR';
    const isCompleted = normalizedStatus === 'COMPLETED' || normalizedStatus === 'DONE';
    const isPending = normalizedStatus === 'PENDING' || normalizedStatus === 'RUNNING' || normalizedStatus === 'IN_PROGRESS';

    useEffect(() => {
        if (open && queryId) fetchQueryResult(queryId);
        if (!open) {
            reset();
            resetCount();
            resetExport();
            setExportDialogOpen(false);
        }
    }, [open, queryId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fallback: the SSE listener below covers most cases, but polling result status
    // to COMPLETED must also refresh the notifications panel in case the SSE event was missed.
    useEffect(() => {
        if (isCompleted && queryId) {
            NotificationService.refreshFromRest().catch(() => {});
        }
    }, [isCompleted, queryId]);

    useEffect(() => {
        if (!open || !queryId) return;

        const handleNewNotification = (notification: Notification) => {
            const notifQueryId = notification.queryId ? String(notification.queryId) : null;
            if (!notifQueryId || notifQueryId !== String(queryId)) return;
            fetchQueryResult(queryId, { keepPreviousResult: true });
        };

        NotificationService.on('new-notification', handleNewNotification);
        return () => {
            NotificationService.removeListener('new-notification', handleNewNotification);
        };
    }, [open, queryId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || !queryId || !isPending) return;

        const interval = setInterval(() => {
            fetchQueryResult(queryId, { keepPreviousResult: true });
        }, 5000);

        return () => clearInterval(interval);
    }, [open, queryId, isPending]); // eslint-disable-line react-hooks/exhaustive-deps

    const countTotal = result?.count ?? result?.listCount?.reduce((acc, i) => acc + i.count, 0);
    const hasExportableResults = isCompleted && typeof countTotal === 'number' && countTotal > 0;
    const canRelaunchCount = !!queryId && !loading && (isExpired || isError);
    const canExport = !!queryId && !!result?.taskId && hasExportableResults;
    const displayError = error || relaunchError;
    const defaultExportEmail = userInfos?.email || '';

    const handleRelaunchCount = async () => {
        if (!queryId) return;

        const response = await launchQueryCount(queryId);
        if (response) {
            if (response.taskId) {
                markAsRunning(response.taskId);
            } else if (response.count !== undefined) {
                await fetchQueryResult(queryId);
            }
        }
    };

    const handleExportQuery = async ({ notify, canal }: { notify: string[]; canal: string }) => {
        if (!queryId || !result?.taskId) return;

        const toastId = toast.loading(t('pages.clients.queryBuilder.exportDialog.requestInProgress'));
        try {
            await extractPersonQuery({
                taskId: result.taskId,
                queryId,
                notify,
                canal,
            });

            setExportDialogOpen(false);
            toast.update(toastId, {
                render: t('pages.clients.queryBuilder.exportDialog.requestSuccess'),
                type: 'success',
                isLoading: false,
                autoClose: 5000,
            });
        } catch (err) {
            toast.update(toastId, {
                render: err instanceof Error ? err.message : exportError || t('pages.clients.queryBuilder.exportDialog.requestError'),
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChartIcon sx={{ color: '#1976d2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {t('pages.clients.queryBuilder.resultDialog.title')}
                        {description && ` : ${description}`}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, minHeight: 200 }}>
                {loading && !result && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t('pages.clients.queryBuilder.resultDialog.loading')}
                        </Typography>
                    </Box>
                )}

                {displayError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {displayError}
                    </Alert>
                )}

                {result && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {isExpired && (
                            <Alert severity="warning" icon={<HourglassEmptyIcon />}>
                                {t('pages.clients.queryBuilder.resultDialog.expiredMessage')}
                            </Alert>
                        )}

                        {isError && (
                            <Alert severity="error">
                                <strong>{t('pages.clients.queryBuilder.resultDialog.failedTitle')}</strong>
                                {result.message?.includes('bad SQL grammar') ? (
                                    <> {t('pages.clients.queryBuilder.resultDialog.invalidSqlMessage')}
                                    {result.message && (
                                        <Box component="pre" sx={{ mt: 1, fontSize: '0.7rem', whiteSpace: 'pre-wrap', opacity: 0.7 }}>
                                            {result.message}
                                        </Box>
                                    )}
                                    </>
                                ) : (
                                    <> {result.message || t('pages.clients.queryBuilder.resultDialog.genericErrorMessage')}</>
                                )}
                            </Alert>
                        )}

                        {isPending && (
                            <Box
                                sx={{
                                    p: 2.5,
                                    border: '1px solid #90caf9',
                                    borderRadius: 1,
                                    bgcolor: '#e3f2fd',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CircularProgress size={22} thickness={5} />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d47a1' }}>
                                            {t('pages.clients.queryBuilder.resultDialog.countingTitle')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#1565c0' }}>
                                            {t('pages.clients.queryBuilder.resultDialog.countingMessage')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {isCompleted && result.listCount && result.listCount.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#495057' }}>
                                    {t('pages.clients.queryBuilder.resultDialog.channelBreakdown')}
                                </Typography>
                                <CountCards listCount={result.listCount} />
                            </Box>
                        )}

                        {isCompleted && !result.listCount && result.count !== undefined && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    p: 3,
                                    border: '2px solid #1976d2',
                                    borderRadius: 2,
                                    bgcolor: '#e3f2fd',
                                }}
                            >
                                <PeopleAltIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1976d2' }}>
                                    {result.count.toLocaleString(formatLocale)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('pages.clients.queryBuilder.resultDialog.clientsInSegment')}
                                </Typography>
                            </Box>
                        )}

                        {isCompleted && result.personIds && result.personIds.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#495057' }}>
                                    {t('pages.clients.queryBuilder.resultDialog.personIdsList', { count: result.personIds.length.toLocaleString(formatLocale) })}
                                </Typography>
                                <PersonIdsTable personIds={result.personIds} />
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, justifyContent: canRelaunchCount || canExport ? 'space-between' : 'flex-end' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                {canRelaunchCount && (
                    <Button
                        onClick={handleRelaunchCount}
                        variant="contained"
                        size="small"
                        startIcon={relaunchingCount ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
                        disabled={relaunchingCount}
                    >
                        {relaunchingCount ? t('pages.clients.queryBuilder.resultDialog.relaunching') : t('pages.clients.queryBuilder.resultDialog.relaunchCount')}
                    </Button>
                )}
                {canExport && (
                    <Button
                        onClick={() => setExportDialogOpen(true)}
                        variant="contained"
                        size="small"
                        startIcon={<FileDownloadIcon />}
                    >
                        {t('pages.clients.queryBuilder.resultDialog.export')}
                    </Button>
                )}
                </Box>
                <Button onClick={onClose} variant="outlined" size="small">
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>

        <QueryExportDialog
            open={exportDialogOpen}
            defaultEmail={defaultExportEmail}
            loading={exporting}
            error={exportError}
            onClose={() => setExportDialogOpen(false)}
            onConfirm={handleExportQuery}
        />
        </>
    );
}

export default QueryResultDialog;
